import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCcw, Mail, Phone } from "lucide-react";
import { getEnquiriesAPI } from "../services/apicollection";

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const EnquiryManagementPage = () => {
  const controller = new AbortController();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getEnquiriesAPI({ signal: controller.signal });
      if (res?.status && Array.isArray(res.data)) {
        setEnquiries(res.data);
      } else {
        setEnquiries([]);
        setError(res?.message || "Failed to load enquiries");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load enquiries");
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
    return () => {
      controller.abort();
    };
  }, [fetchEnquiries]);

  const sortedEnquiries = [...enquiries].sort((a, b) => {
    const at = new Date(a?.createdAt || 0).getTime();
    const bt = new Date(b?.createdAt || 0).getTime();
    return bt - at;
  });

  const total = sortedEnquiries.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndexExclusive = Math.min(startIndex + pageSize, total);
  const pageRows = sortedEnquiries.slice(startIndex, endIndexExclusive);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Enquiries</h2>
        <button
          type="button"
          onClick={() => fetchEnquiries()}
          className="inline-flex items-center gap-2 rounded-lg bg-[#0f1c2e] px-3 py-2 text-sm font-medium text-slate-200 ring-1 ring-slate-200 transition-colors hover:bg-[#0d1b2e]"
          title="Refresh"
        >
          <RefreshCcw size={16} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-700">
          {error}
        </div>
      ) : sortedEnquiries.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center">
          <Mail size={36} className="text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No enquiries yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
            <div>
              Showing{" "}
              <span className="font-semibold text-slate-200">
                {startIndex + 1}–{endIndexExclusive}
              </span>{" "}
              of <span className="font-semibold text-slate-200">{total}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="rounded-md bg-[#0f1c2e] px-2 py-1 ring-1 ring-slate-200 hover:bg-[#0d1b2e] disabled:opacity-60"
              >
                Prev
              </button>
              <span className="tabular-nums">
                Page <span className="font-semibold">{safePage}</span> /{" "}
                <span className="font-semibold">{totalPages}</span>
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="rounded-md bg-[#0f1c2e] px-2 py-1 ring-1 ring-slate-200 hover:bg-[#0d1b2e] disabled:opacity-60"
              >
                Next
              </button>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl bg-[#0f1c2e] shadow-[0_20px_50px_rgba(0,0,0,0.08)]">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead className="border-b border-[#1d3350] bg-[#0d1b2e]">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Name
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Email
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Phone
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Course
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      College
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      City
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Message
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pageRows.map((e) => (
                    <tr
                      key={e?._id}
                      className="transition-colors hover:bg-[#0d1b2e]"
                    >
                      <td className="px-6 py-4 text-sm text-slate-200 whitespace-nowrap capitalize">
                        {e?.fullname || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                        <a
                          href={`mailto:${e?.email}`}
                          className="text-blue-600 hover:underline inline-flex items-center gap-1"
                        >
                          <Mail size={14} />
                          {e?.email || "—"}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                        <a
                          href={`tel:${e?.number}`}
                          className="text-blue-600 hover:underline inline-flex items-center gap-1"
                        >
                          <Phone size={14} />
                          {e?.number || "—"}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap capitalize">
                        {e?.course || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap capitalize">
                        {e?.college || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap capitalize">
                        {e?.city || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300 max-w-[200px]">
                        <div className="line-clamp-2">{e?.message || "—"}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                        {formatDateTime(e?.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquiryManagementPage;
